// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="QrCode" set="mytaible-shadcn-studio" verified=tbd
import { Download } from 'lucide-react';
import QRCode from 'qrcode';
import * as React from 'react';
import { cn } from '../../lib/utils';

export interface QrCodeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The URL or text to encode */
  value: string;
  /** Canvas size in pixels (default: 200) */
  size?: number;
  /** Error correction level (default: 'M') */
  level?: 'L' | 'M' | 'Q' | 'H';
  /** When provided, a download button appears with this filename (no extension needed) */
  downloadFileName?: string;
}

const QrCode = React.forwardRef<HTMLDivElement, QrCodeProps>(
  ({ value, size = 200, level = 'M', downloadFileName, className, ...props }, ref) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      setError(null);
      QRCode.toCanvas(canvas, value, {
        width: size,
        errorCorrectionLevel: level,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      }).catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to generate QR code');
      });
    }, [value, size, level]);

    function handleDownload() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${downloadFileName ?? 'qr-code'}.png`;
      a.click();
    }

    return (
      <div
        ref={ref}
        className={cn('inline-flex flex-col items-center gap-2', className)}
        {...props}
      >
        {error ? (
          <div
            className="flex items-center justify-center rounded-md border border-destructive bg-destructive/10 text-destructive text-sm"
            style={{ width: size, height: size }}
          >
            QR error
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            aria-label={`QR code for: ${value}`}
            role="img"
            className="rounded-md"
          />
        )}

        {downloadFileName && !error && (
          <button
            type="button"
            onClick={handleDownload}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
              'border border-border bg-background text-muted-foreground',
              'hover:bg-muted hover:text-foreground transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <Download className="size-3.5" aria-hidden="true" />
            Download PNG
          </button>
        )}
      </div>
    );
  },
);
QrCode.displayName = 'QrCode';

export { QrCode };
